import { useState, useEffect, useCallback } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faBolt, faPlus, faUsers, faCircle, faTrash, faSpinner, faChevronUp, faChevronDown, faEye, faVideo, faUser, faXmark, faFileCsv } from '@/fontawesome';
import { APIRoutes } from '@/services/api.routes';
import { useUser } from '@/contexts/UserContext';
import { DAY_TRADING_ROUTES } from '@/constants/routes';
import {formatCurrency, formatDate, formatLongDuration} from '@/utils/helpers';
import { calculateSessionProfit, calculatePersonalSessionProfit, formatProfitPct, getProfitColorClass } from '@/utils/daytrade-session-profit';
import PageBasedPagination from '@/components/ui/PageBasedPagination';
import Modal from '@/components/ui/Modal';
import type { DayTradingSession } from '@/models/day-trading-session';
import type { DayTradingSessionID } from '@/models/ids';
import { showErrorToast, showSuccessToast } from '@/utils/toasts';
import TradingDisclaimerModal from '@/components/signale/TradingDisclaimerModal';
import { cleanYouTubeUrl, isValidYouTubeUrl } from '@/utils/youtube-url';
import { isValidGoogleMeetUrl } from '@/utils/google-meet-url';
import { isValidDiscordUrl, DEFAULT_DISCORD_URL } from '@/utils/discord-url';
import StreamURLInput from '@/components/daytrading/components/StreamURLInput';
import { ALL_STREAM_MODES, STREAM_MODE_LABELS } from '@/models/enums';
import type { StreamMode } from '@/models/enums';
import Tooltip from '../ui/Tooltip';
import { useFeatureAccess } from '@/hooks/useFeatureAccess';
import DayTradingResultsGraph from '@/components/daytrading/components/DayTradingResultsGraph';
import { downloadDayTradesCsv } from '@/utils/daytrade-csv-export';

const PAGE_SIZE = 25;

export default function DayTradingSessionsList() {
  const navigate = useNavigate();
  const { isStaff, isAdmin } = useUser();
  const { hasFeature } = useFeatureAccess();
  const showGraph = hasFeature('dayTrading');

  const [openSession, setOpenSession] = useState<DayTradingSession | null>(null);
  const [openLoading, setOpenLoading] = useState(true);

  const [pastSessions, setPastSessions] = useState<DayTradingSession[]>([]);
  const [total, setTotal] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [listLoading, setListLoading] = useState(true);

  const [allSessions, setAllSessions] = useState<DayTradingSession[]>([]);

  const [joining, setJoining] = useState(false);
  const [creating, setCreating] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [createStreamMode, setCreateStreamMode] = useState<StreamMode>('NONE');
  const [youtubeUrlInput, setYoutubeUrlInput] = useState('');
  const [googleMeetUrlInput, setGoogleMeetUrlInput] = useState('');
  const [discordUrlInput, setDiscordUrlInput] = useState(DEFAULT_DISCORD_URL);
  const [createdDirectSession, setCreatedDirectSession] = useState<DayTradingSession | null>(null);
  const [sessionToDelete, setSessionToDelete] = useState<DayTradingSessionID | null>(null);
  const [deletingSession, setDeletingSession] = useState<DayTradingSessionID | null>(null);
  const [exportingCsv, setExportingCsv] = useState(false);

  const fetchOpenSession = useCallback(async () => {
    try {
      const response = await APIRoutes.listDayTradingSessions({ open: true, limit: 1 });
      setOpenSession(response.sessions.length > 0 ? response.sessions[0] : null);
    } catch {
      // Silently fail
    } finally {
      setOpenLoading(false);
    }
  }, []);

  const fetchSessions = useCallback(async () => {
    setListLoading(true);
    try {
      const cursorToken = currentPage > 1 ? `$${currentPage}` : undefined;
      const response = await APIRoutes.listDayTradingSessions({
        limit: PAGE_SIZE,
        next: cursorToken,
        open: false,
        sort: 'start',
        sortDirection: 'DESC',
      });
      setPastSessions(response.sessions);
      setTotal(response.total);
    } catch {
      // Silently fail
    } finally {
      setListLoading(false);
    }
  }, [currentPage]);

  const fetchAllSessionsForGraph = useCallback(async () => {
    if (!showGraph) return;
    try {
      const response = await APIRoutes.listDayTradingSessions({
        limit: 1000,
        open: false,
        sort: 'start',
        sortDirection: 'ASC',
      });
      setAllSessions(response.sessions);
    } catch {
      // Silently fail — graph will just be empty
    }
  }, [showGraph]);

  useEffect(() => {
    fetchOpenSession();
  }, [fetchOpenSession]);

  useEffect(() => {
    fetchSessions();
  }, [fetchSessions]);

  useEffect(() => {
    fetchAllSessionsForGraph();
  }, [fetchAllSessionsForGraph]);

  const handleJoinAsParticipant = async (sessionId: DayTradingSession['id']) => {
    setJoining(true);
    try {
      await APIRoutes.joinDayTradingSessionAsParticipant(sessionId);
      navigate(DAY_TRADING_ROUTES.PARTICIPATE(sessionId));
    } catch (err) {
      console.error(err);
      showErrorToast('Fehler: Konnte der Session nicht beitreten.');
    } finally {
      setJoining(false);
    }
  };

  const handleJoinAsModerator = async (sessionId: DayTradingSession['id']) => {
    setJoining(true);
    try {
      await APIRoutes.joinDayTradingSessionAsModerator(sessionId);
      navigate(DAY_TRADING_ROUTES.MODERATE(sessionId));
    } catch (err) {
      console.error(err);
      showErrorToast('Fehler: Konnte der Session nicht beitreten.');
    } finally {
      setJoining(false);
    }
  };

  const handleCreateSession = async () => {
    // YouTube, Google Meet and Discord all use soft validation: send the
    // canonical form when we can recognize it, otherwise fall back to the
    // raw input and let the backend store whatever the user typed.
    let cleanedYoutubeURL = '';
    let googleMeetURL = '';
    let discordURL = '';
    if (createStreamMode === 'YOUTUBE') {
      cleanedYoutubeURL = cleanYouTubeUrl(youtubeUrlInput) ?? youtubeUrlInput.trim();
    }
    if (createStreamMode === 'GOOGLE_MEET') {
      googleMeetURL = googleMeetUrlInput.trim();
    }
    if (createStreamMode === 'DISCORD') {
      discordURL = discordUrlInput.trim();
    }
    setCreating(true);
    try {
      const session = await APIRoutes.createDayTradingSession({
        streamMode: createStreamMode,
        youtubeURL: cleanedYoutubeURL,
        googleMeetURL: googleMeetURL,
        discordURL: discordURL,
      });
      await APIRoutes.joinDayTradingSessionAsModerator(session.id);

      // For DIRECT mode, surface the ingest URL + key one last time before the
      // moderator leaves the create flow. The same info is available in the
      // edit-stream modal on the moderator page.
      if (createStreamMode === 'DIRECT') {
        setCreatedDirectSession(session);
        return;
      }

      navigate(DAY_TRADING_ROUTES.MODERATE(session.id));
    } catch (err) {
      console.error(err);
      showErrorToast('Fehler: Neue Session konnte nicht erstellt werden.');
    } finally {
      setCreating(false);
    }
  };

  const handleDeleteSession = async () => {
    if (sessionToDelete === null) return;
    setDeletingSession(sessionToDelete);
    try {
      await APIRoutes.deleteDayTradingSession(sessionToDelete);
      setPastSessions((prev) => prev.filter((s) => s.id !== sessionToDelete));
      setAllSessions((prev) => prev.filter((s) => s.id !== sessionToDelete));
      setTotal((prev) => prev - 1);
      if (openSession !== null && openSession.id === sessionToDelete) {
        setOpenSession(null);
      }
      showSuccessToast('Session wurde gelöscht.');
      setSessionToDelete(null);
    } catch (err) {
      console.error(err);
      showErrorToast('Fehler: Session konnte nicht gelöscht werden.');
    } finally {
      setDeletingSession(null);
    }
  };

  const handleExportCsv = async () => {
    setExportingCsv(true);
    try {
      await downloadDayTradesCsv();
    } catch (err) {
      console.error(err);
      showErrorToast('Fehler: Trades konnten nicht exportiert werden.');
    } finally {
      setExportingCsv(false);
    }
  };

  return (
    <div>
      <TradingDisclaimerModal />
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900 dark:text-white">
          Daytrading
        </h1>
        <p className="mt-2 text-gray-600 dark:text-gray-400">
          Live Daytrading Sessions
        </p>
      </div>

      {/* Open Session Card */}
      {openLoading ? (
        <div className="mb-8 rounded-xl border-2 border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 p-6 animate-pulse">
          <div className="h-6 bg-gray-200 dark:bg-gray-700 rounded w-1/3 mb-4" />
          <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-1/2" />
        </div>
      ) : openSession !== null ? (
        <div className="mb-8 rounded-xl border-2 border-green-400 dark:border-green-600 bg-white dark:bg-gray-800 p-6 shadow-sm">
          <div className="flex items-center justify-between flex-wrap gap-4">
            <div className="flex items-center gap-3">
              <FontAwesomeIcon icon={faBolt} className="w-5 h-5 text-green-500" />
              <div>
                <div className="flex items-center gap-2">
                  <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
                    Aktive Session
                  </h2>
                  <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 dark:bg-green-900/50 text-green-700 dark:text-green-300 animate-pulse">
                    <FontAwesomeIcon icon={faCircle} className="w-2 h-2" />
                    Live
                  </span>
                </div>
                <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                  Gestartet: {formatDate(openSession.start)}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              {isStaff ? (
                <>
                  <button
                    onClick={() => handleJoinAsParticipant(openSession.id)}
                    disabled={joining}
                    className="px-4 py-2 text-sm font-medium rounded-lg border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer transition-colors"
                  >Als Teilnehmer beitreten</button>
                  <button
                    onClick={() => handleJoinAsModerator(openSession.id)}
                    disabled={joining}
                    className="px-4 py-2 text-sm font-medium rounded-lg bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer transition-colors"
                  >
                    Als Moderator beitreten
                  </button>
                  {isAdmin && (
                    <Link
                      to={DAY_TRADING_ROUTES.OBSERVE(openSession.id)}
                      className="p-2 text-gray-500 dark:text-gray-400 hover:text-blue-600 dark:hover:text-blue-400 transition-colors"
                      title="Beobachten"
                    >
                      <FontAwesomeIcon icon={faEye} className="w-4 h-4" />
                    </Link>
                  )}
                  {isAdmin && (
                    <button
                      onClick={() => setSessionToDelete(openSession.id)}
                      disabled={deletingSession === openSession.id}
                      className="p-2 text-red-600 dark:text-red-400 hover:text-red-900 dark:hover:text-red-300 cursor-pointer transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <FontAwesomeIcon icon={deletingSession === openSession.id ? faSpinner : faTrash} className="w-4 h-4" spin={deletingSession === openSession.id} />
                    </button>
                  )}
                </>
              ) : (
                openSession.participant === null ? (
                  <button
                    onClick={() => handleJoinAsParticipant(openSession.id)}
                    disabled={joining}
                    className="px-4 py-2 text-sm font-medium rounded-lg bg-green-600 text-white hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer transition-colors"
                  >Beitreten</button>
                ) : (
                  <button
                    onClick={() => navigate(DAY_TRADING_ROUTES.PARTICIPATE(openSession?.id))}
                    disabled={joining}
                    className="px-4 py-2 text-sm font-medium rounded-lg bg-green-600 text-white hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer transition-colors"
                  >Öffnen</button>
                )
              )}
            </div>
          </div>
        </div>
      ) : (
        <div className="mb-8 rounded-xl border-2 border-dashed border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 p-6">
          <p className="text-gray-500 dark:text-gray-400 text-center">
            Aktuell keine aktive Session
          </p>
        </div>
      )}

      {/* Staff: Create Session */}
      {isStaff && (
        <div className="mb-8">
          <button
            onClick={() => { setShowCreateModal(true); }}
            disabled={creating || openSession !== null}
            className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-lg bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer transition-colors"
          >
            <FontAwesomeIcon icon={faPlus} className="w-4 h-4" />
            Neue Session starten
          </button>
        </div>
      )}

      {/* Past Sessions List */}
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between gap-4">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
            Vergangene Sessions
          </h2>
          {isAdmin && (
            <Tooltip text={'Alle Trades aller Sessions als CSV herunterladen.\nDepotabhängige Werte basieren auf einem Depot mit $50.000.'}>
              <button
                onClick={handleExportCsv}
                disabled={exportingCsv}
                className="inline-flex items-center gap-2 px-3 py-1.5 text-xs font-medium rounded-lg border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer transition-colors"
              >
                <FontAwesomeIcon icon={exportingCsv ? faSpinner : faFileCsv} className="w-3.5 h-3.5" spin={exportingCsv} />
                Alle Trades exportieren
              </button>
            </Tooltip>
          )}
        </div>

        {listLoading ? (
          <div className="p-6 space-y-3">
            {[...Array(5)].map((_, i) => (
              <div key={i} className="h-12 bg-gray-100 dark:bg-gray-700 rounded animate-pulse" />
            ))}
          </div>
        ) : pastSessions.length === 0 ? (
          <div className="p-6 text-center text-gray-500 dark:text-gray-400">
            Noch keine Sessions vorhanden
          </div>
        ) : (
          <>
            {/* Desktop Table */}
            <div className="hidden md:block overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50 dark:bg-gray-700/50">
                  <tr>
                    
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Datum</th>
                    
                    {isStaff && ( <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider"># Teilnehmer</th> )}
                    
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider"># Trades</th>
                    
                    {isStaff && ( <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Dauer</th> )}
                    
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Theoretisches Ergebnis</th>

                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Persönliches Ergebnis</th>

                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Aktionen</th>
                  
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                  {pastSessions.map((session) => {
                    const canOpen = isStaff || session.participant !== null;
                    return (
                      <tr key={session.id} className="hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors">
                        <td className="px-6 py-4 text-sm text-gray-900 dark:text-white">
                          {formatDate(session.start)}
                        </td>
                        
                        {isStaff && ( <td className="px-6 py-4 text-sm text-gray-500 dark:text-gray-400">{session.participants?.length ?? '-'}</td> )}
                        
                        <td className="px-6 py-4 text-sm text-gray-500 dark:text-white">{session.state.trades?.filter(t => ['GOAL_REACHED', 'STOPLOSS'].includes(t.state))?.length ?? '-'}<span style={{'opacity':0.3}}> / {session.state.trades?.length ?? '-'}</span></td>
                        
                        {isStaff && ( <td className="px-6 py-4 text-sm text-gray-500 dark:text-gray-400">{ formatLongDuration( ((new Date(session.end!)).getTime() - (new Date(session.start)).getTime()) / 1000 ) }</td> )}
                        
                        <td className="px-6 py-4 text-right">
                          {(() => {
                            const trades = session.state.trades ?? [];
                            if (trades.length === 0) {
                              return <span className="text-sm text-gray-400 dark:text-gray-500">-</span>;
                            }
                            const { profitPct, budget, profit } = calculateSessionProfit(trades);
                            const isPositive = profitPct > 0;
                            const isNegative = profitPct < 0;
                            return (
                              <Tooltip text={`Basierend auf einem Depot mit $${formatCurrency(budget, 'USD')}:\n${formatCurrency(profit, 'USD')} Profit`}>
                                <span className={`inline-flex items-center gap-1 text-sm font-semibold ${getProfitColorClass(profitPct)}`}>
                                  {isPositive && (<><FontAwesomeIcon icon={faChevronUp} className="w-3 h-3" /> +{formatProfitPct(profitPct)}%</>)}
                                  {isNegative && (<><FontAwesomeIcon icon={faChevronDown} className="w-3 h-3" /> {formatProfitPct(profitPct)}%</>)}

                                </span>
                              </Tooltip>
                            );
                          })()}
                        </td>

                        <td className="px-6 py-4 text-right">
                          {(() => {
                            const trades = session.state.trades ?? [];
                            const personal = calculatePersonalSessionProfit(trades, session.participant);
                            if (personal === null) {
                              return <span className="text-sm text-gray-400 dark:text-gray-500">-</span>;
                            }
                            const { profitPct, budget, invested, profit } = personal;
                            const isPositive = profitPct > 0;
                            const isNegative = profitPct < 0;
                            return (
                              <Tooltip text={`Basierend auf deinem Depot mit $${formatCurrency(budget, 'USD')}:\n$${formatCurrency(profit, 'USD')} Profit bei $${formatCurrency(invested, 'USD')} investiertem Kapital.`}>
                                <span className={`inline-flex items-center gap-1 text-sm font-semibold ${getProfitColorClass(profitPct)}`}>
                                  {isPositive && (<><FontAwesomeIcon icon={faChevronUp} className="w-3 h-3" /> +{formatProfitPct(profitPct)}%</>)}
                                  {isNegative && (<><FontAwesomeIcon icon={faChevronDown} className="w-3 h-3" /> {formatProfitPct(profitPct)}%</>)}
                                </span>
                              </Tooltip>
                            );
                          })()}
                        </td>

                        <td className="px-6 py-4 text-right">
                          <div className="flex items-center justify-end gap-2">
                            {canOpen ? (
                              <Link
                                to={DAY_TRADING_ROUTES.PARTICIPATE(session.id)}
                                className="px-3 py-1.5 text-xs font-medium rounded-lg border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                              >Öffnen</Link>
                            ) : (
                              <UnavailableSessionSpan />
                            )}
                            {isStaff && (
                              <Link to={DAY_TRADING_ROUTES.MODERATE(session.id)} className="px-3 py-1.5 text-xs font-medium rounded-lg bg-blue-600 text-white hover:bg-blue-700 transition-colors">
                                Moderation
                              </Link>
                            )}
                            {isAdmin && (
                              <Link
                                to={DAY_TRADING_ROUTES.OBSERVE(session.id)}
                                className="p-1.5 text-gray-500 dark:text-gray-400 hover:text-blue-600 dark:hover:text-blue-400 transition-colors"
                                title="Beobachten"
                              >
                                <FontAwesomeIcon icon={faEye} className="w-3.5 h-3.5" />
                              </Link>
                            )}
                            {isAdmin && (
                              <button
                                onClick={() => setSessionToDelete(session.id)}
                                disabled={deletingSession === session.id}
                                className="p-1.5 text-red-600 dark:text-red-400 hover:text-red-900 dark:hover:text-red-300 cursor-pointer transition-colors disabled:opacity-50 disabled:cursor-not-allowed">
                                <FontAwesomeIcon icon={deletingSession === session.id ? faSpinner : faTrash} className="w-3.5 h-3.5" spin={deletingSession === session.id} />
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {/* Mobile Cards */}
            <div className="md:hidden divide-y divide-gray-200 dark:divide-gray-700">
              {pastSessions.map((session) => {
                const canOpen = isStaff || session.participant !== null;
                return (
                  <div key={session.id} className="p-4 space-y-2">
                    <div className="flex items-center justify-between">
                      <span className="text-sm font-medium text-gray-900 dark:text-white">
                        {formatDate(session.start)}
                      </span>
                      {session.end === null ? (
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 dark:bg-green-900/50 text-green-700 dark:text-green-300">
                          <FontAwesomeIcon icon={faCircle} className="w-2 h-2" />
                          Offen
                        </span>
                      ) : (
                        <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400">
                          Beendet
                        </span>
                      )}
                    </div>
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-4 text-xs text-gray-500 dark:text-gray-400">
                        {isStaff && (
                          <span>
                            <FontAwesomeIcon icon={faUsers} className="w-3 h-3 mr-1" />
                            {session.participants?.length}
                          </span>
                        )}
                        {(() => {
                          const trades = session.state.trades ?? [];
                          if (trades.length === 0) return null;
                          const { profitPct } = calculateSessionProfit(trades);
                          const isPositive = profitPct > 0;
                          const isNegative = profitPct < 0;
                          return (
                            <span className={`inline-flex items-center gap-1 text-xs font-semibold ${getProfitColorClass(profitPct)}`}>
                              {isPositive && <FontAwesomeIcon icon={faChevronUp} className="w-2.5 h-2.5" />}
                              {isNegative && <FontAwesomeIcon icon={faChevronDown} className="w-2.5 h-2.5" />}
                              {formatProfitPct(profitPct)}%
                            </span>
                          );
                        })()}
                        {(() => {
                          const trades = session.state.trades ?? [];
                          const personal = calculatePersonalSessionProfit(trades, session.participant);
                          if (personal === null) return null;
                          const { profitPct } = personal;
                          const isPositive = profitPct > 0;
                          const isNegative = profitPct < 0;
                          return (
                            <span className={`inline-flex items-center gap-1 text-xs font-semibold ${getProfitColorClass(profitPct)}`}>
                              <FontAwesomeIcon icon={faUser} className="w-2.5 h-2.5" />
                              {isPositive && <FontAwesomeIcon icon={faChevronUp} className="w-2.5 h-2.5" />}
                              {isNegative && <FontAwesomeIcon icon={faChevronDown} className="w-2.5 h-2.5" />}
                              {formatProfitPct(profitPct)}%
                            </span>
                          );
                        })()}
                      </div>
                      <div className="flex items-center gap-2">
                        {canOpen ? (
                          <Link
                            to={DAY_TRADING_ROUTES.PARTICIPATE(session.id)}
                            className="px-3 py-1.5 text-xs font-medium rounded-lg border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                          >
                            Öffnen
                          </Link>
                        ) : (
                          <UnavailableSessionSpan />
                        )}
                        {isStaff && (
                          <Link
                            to={DAY_TRADING_ROUTES.MODERATE(session.id)}
                            className="px-3 py-1.5 text-xs font-medium rounded-lg bg-blue-600 text-white hover:bg-blue-700 transition-colors"
                          >
                            Moderation
                          </Link>
                        )}
                        {isAdmin && (
                          <Link
                            to={DAY_TRADING_ROUTES.OBSERVE(session.id)}
                            className="p-1.5 text-gray-500 dark:text-gray-400 hover:text-blue-600 dark:hover:text-blue-400 transition-colors"
                            title="Beobachten"
                          >
                            <FontAwesomeIcon icon={faEye} className="w-3.5 h-3.5" />
                          </Link>
                        )}
                        {isAdmin && (
                          <button
                            onClick={() => setSessionToDelete(session.id)}
                            disabled={deletingSession === session.id}
                            className="p-1.5 text-red-600 dark:text-red-400 hover:text-red-900 dark:hover:text-red-300 cursor-pointer transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                          >
                            <FontAwesomeIcon icon={deletingSession === session.id ? faSpinner : faTrash} className="w-3.5 h-3.5" spin={deletingSession === session.id} />
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>

            <PageBasedPagination
              currentPage={currentPage}
              totalCount={total}
              pageSize={PAGE_SIZE}
              onPageChange={setCurrentPage}
              loading={listLoading}
            />
          </>
        )}
      </div>

      {showGraph && (
        <DayTradingResultsGraph sessions={allSessions} />
      )}

      <Modal
        isOpen={sessionToDelete !== null}
        onClose={() => setSessionToDelete(null)}
        title="Session löschen"
        icon={<FontAwesomeIcon icon={faTrash} className="w-5 h-5 text-red-600" />}
        maxWidth="sm"
        closeDisabled={deletingSession !== null}
      >
        <div className="p-6">
          <p className="text-gray-700 dark:text-gray-300 mb-6">
            Möchtest du diese Session wirklich löschen?
          </p>
          <div className="flex justify-end gap-3">
            <button
              onClick={() => setSessionToDelete(null)}
              disabled={deletingSession !== null}
              className="px-4 py-2 text-sm font-medium rounded-lg border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer transition-colors"
            >
              Abbrechen
            </button>
            <button
              onClick={handleDeleteSession}
              disabled={deletingSession !== null}
              className="px-4 py-2 text-sm font-medium rounded-lg bg-red-600 text-white hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer transition-colors"
            >
              {deletingSession !== null ? (
                <FontAwesomeIcon icon={faSpinner} className="w-4 h-4" spin />
              ) : (
                'Löschen'
              )}
            </button>
          </div>
        </div>
      </Modal>

      {/* Create session modal */}
      <Modal
        isOpen={showCreateModal && createdDirectSession === null}
        onClose={() => { setShowCreateModal(false); setCreateStreamMode('NONE'); }}
        title="Neue Session starten"
        icon={<FontAwesomeIcon icon={faVideo} className="w-5 h-5 text-blue-600" />}
        maxWidth="2xl"
        closeDisabled={creating}
      >
        <div className="p-6 space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Stream-Modus
            </label>
            <div className="grid grid-cols-2 sm:grid-cols-5 gap-2">
              {ALL_STREAM_MODES.map((mode) => (
                <button
                  key={mode}
                  type="button"
                  onClick={() => setCreateStreamMode(mode)}
                  disabled={creating}
                  className={`px-3 py-2 text-sm font-medium rounded-lg border transition-colors cursor-pointer ${
                    createStreamMode === mode
                      ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/40 text-blue-700 dark:text-blue-200'
                      : 'border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700'
                  }`}
                >
                  {STREAM_MODE_LABELS[mode]}
                </button>
              ))}
            </div>
          </div>

          {createStreamMode === 'YOUTUBE' && (
            <StreamURLInput
              label="YouTube URL"
              placeholder="https://www.youtube.com/watch?v=..."
              value={youtubeUrlInput}
              onChange={setYoutubeUrlInput}
              disabled={creating}
              warning={isValidYouTubeUrl(youtubeUrlInput) ? null : 'Warnung: URL wird nicht als YouTube-Link erkannt.'}
            />
          )}

          {createStreamMode === 'DIRECT' && (
            <p className="text-xs text-gray-500 dark:text-gray-400">
              Nach dem Erstellen erhältst du eine OBS-URL und einen Stream-Key, mit denen du den Direkt-Stream starten kannst.
            </p>
          )}

          {createStreamMode === 'GOOGLE_MEET' && (
            <StreamURLInput
              label="Google Meet URL"
              placeholder="https://meet.google.com/abc-defg-hij"
              value={googleMeetUrlInput}
              onChange={setGoogleMeetUrlInput}
              disabled={creating}
              warning={isValidGoogleMeetUrl(googleMeetUrlInput) ? null : 'Warnung: URL sollte mit „https://meet.google.com/" beginnen.'}
            />
          )}

          {createStreamMode === 'DISCORD' && (
            <StreamURLInput
              label="Discord URL"
              placeholder="https://discord.com/channels/..."
              value={discordUrlInput}
              onChange={setDiscordUrlInput}
              disabled={creating}
              warning={isValidDiscordUrl(discordUrlInput) ? null : 'Warnung: URL sollte mit „https://discord.com/channels/" beginnen.'}
            />
          )}

          <div className="flex justify-end gap-3">
            <button
              onClick={() => { setShowCreateModal(false); setYoutubeUrlInput(''); setGoogleMeetUrlInput(''); setDiscordUrlInput(''); setCreateStreamMode('NONE'); }}
              disabled={creating}
              className="px-4 py-2 text-sm font-medium rounded-lg border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 disabled:opacity-50 cursor-pointer transition-colors"
            >
              Abbrechen
            </button>
            <button
              onClick={handleCreateSession}
              disabled={creating}
              className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-lg bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50 cursor-pointer transition-colors"
            >
              {creating ? (
                <FontAwesomeIcon icon={faSpinner} className="w-4 h-4" spin />
              ) : (
                <>
                  <FontAwesomeIcon icon={faPlus} className="w-4 h-4" />
                  Session starten
                </>
              )}
            </button>
          </div>
        </div>
      </Modal>

      {/* Direct-stream credentials modal — shown immediately after creating a DIRECT session */}
      <Modal
        isOpen={createdDirectSession !== null}
        onClose={() => {
          if (createdDirectSession !== null) {
            navigate(DAY_TRADING_ROUTES.MODERATE(createdDirectSession.id));
            setCreatedDirectSession(null);
            setShowCreateModal(false);
          }
        }}
        title="Direkt-Stream eingerichtet"
        icon={<FontAwesomeIcon icon={faVideo} className="w-5 h-5 text-blue-600" />}
        maxWidth="md"
      >
        {createdDirectSession !== null && (
          <div className="p-6 space-y-4">
            <p className="text-sm text-gray-700 dark:text-gray-300">
              Trage diese Werte in OBS unter <strong>Einstellungen → Stream</strong> ein. Sie sind nur jetzt und im Edit-Dialog der Session sichtbar.
            </p>
            <div>
              <label className="block text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase mb-1">Server (URL)</label>
              <input
                type="text"
                readOnly
                value={createdDirectSession.directStreamIngestURL}
                onClick={(e) => (e.target as HTMLInputElement).select()}
                className="w-full px-3 py-2 text-sm font-mono rounded-lg border border-gray-300 dark:border-gray-600 bg-gray-50 dark:bg-gray-900 text-gray-900 dark:text-white cursor-text"
              />
            </div>
            <div>
              <label className="block text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase mb-1">Stream-Key</label>
              <input
                type="text"
                readOnly
                value={createdDirectSession.directStreamKey}
                onClick={(e) => (e.target as HTMLInputElement).select()}
                className="w-full px-3 py-2 text-sm font-mono rounded-lg border border-gray-300 dark:border-gray-600 bg-gray-50 dark:bg-gray-900 text-gray-900 dark:text-white cursor-text"
              />
            </div>
            <div className="flex justify-end">
              <button
                onClick={() => {
                  navigate(DAY_TRADING_ROUTES.MODERATE(createdDirectSession.id));
                  setCreatedDirectSession(null);
                  setShowCreateModal(false);
                }}
                className="px-4 py-2 text-sm font-medium rounded-lg bg-blue-600 text-white hover:bg-blue-700 cursor-pointer transition-colors"
              >
                Zur Session
              </button>
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
}

const UNAVAILABLE_SESSION_HINT = 'Der Zugriff auf eine vergangene Session ist nur möglich,\nwenn du am Session-Tag dabei warst.';

function UnavailableSessionSpan() {
  return (
    <Tooltip text={UNAVAILABLE_SESSION_HINT}>
      <span
        className="inline-flex items-center gap-2 px-3 py-1.5 text-xs font-medium rounded-lg bg-gray-200 dark:bg-gray-700 text-gray-500 dark:text-gray-400 cursor-not-allowed"
        title={UNAVAILABLE_SESSION_HINT}
      >
        <FontAwesomeIcon icon={faXmark} className="w-3 h-3" />
        Nicht verfügbar
      </span>
    </Tooltip>
  );
}
